import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  getToken,
  setToken as persistToken,
  clearToken,
  getCachedUser,
  setCachedUser,
  clearCachedUser,
} from '../lib/session';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const initialToken = getToken();
  const initialUser = initialToken ? getCachedUser() : null;

  const [user, setUser] = useState(initialUser);
  const [token, setToken] = useState(initialToken);
  // With a token and a cached profile there is nothing to wait for - the app can
  // render at once and revalidate behind the scenes.
  const [loading, setLoading] = useState(!(initialToken && initialUser));

  const api = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  api.interceptors.request.use((config) => {
    const currentToken = getToken();
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail;
        // The backend allows one active device per user; a login elsewhere strands this token.
        if (typeof detail === 'string' && detail.includes('signed out')) {
          try {
            localStorage.setItem('auth_message', detail);
          } catch (storageError) {
            // Private browsing can refuse writes; the sign-out itself still stands.
          }
        }
        clearSession();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Only the server saying "no" ends a session. A cold start on a phone
    // routinely beats the network by a few seconds, and treating that as a
    // rejected login would throw away a token that is still perfectly good.
    const attempt = async (remaining, delayMs) => {
      try {
        const response = await api.get('/auth/me');
        const { refreshed_token: refreshedToken, ...userData } = response.data;
        // The backend slides the expiry once a token is past halfway through its
        // life, so regular use keeps the session alive indefinitely.
        if (refreshedToken) {
          persistToken(refreshedToken);
          setToken(refreshedToken);
        }
        setUser(userData);
        setCachedUser(userData);
        return;
      } catch (error) {
        if (error.response?.status === 401) {
          // A genuine rejection - expired, or the session was signed out.
          clearSession();
          return;
        }
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          return attempt(remaining - 1, Math.min(delayMs * 2, 15000));
        }
        // Offline, or the server is unreachable. Keep the session and carry on
        // with the cached profile; the next request will revalidate.
        console.warn('Could not reach the server; keeping the stored session.', error?.message);
      }
    };

    try {
      await attempt(3, 2000);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    persistToken(access_token);
    setToken(access_token);
    setUser(userData);
    setCachedUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'field_user') => {
    const response = await api.post('/auth/register', { email, password, name, role });
    const { access_token, user: userData } = response.data;
    persistToken(access_token);
    setToken(access_token);
    setUser(userData);
    setCachedUser(userData);
    return userData;
  };

  const clearSession = () => {
    clearToken();
    clearCachedUser();
    setToken(null);
    setUser(null);
  };

  const logout = async () => {
    // Best effort: free the single-device seat so the next sign-in is clean.
    try {
      if (getToken()) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      // An expired or superseded token 401s here; the local teardown below still runs.
    }
    clearSession();
  };

  const isAdmin = user?.role === 'admin';
  // Deleting is reserved for the super admin; every other account only creates and edits.
  const isSuperadmin = Boolean(user?.is_superadmin);
  const canDelete = isSuperadmin;

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, api, isAdmin, isSuperadmin, canDelete }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
